import { toggleGroup } from "../operations/grouping/visibility";
import { groupSizes } from "../helpers/group";

webix.protoUI(
	{
		name: "ssheet-groups",
		defaults: {
			width: 0,
			height: 0,
			hidden: true,
		},
		on_click: {
			webix_ssheet_group_container(e) {
				this._handleClick(e);
				return false;
			},
		},
		$init(config) {
			this._isRow = (config.dimension || "row") === "row";
			const isRow = this._isRow;

			// CSS prop references
			this._mainSizeProp = isRow ? "height" : "width"; // extent along the grouped dimension (main axis)
			this._crossSizeProp = isRow ? "width" : "height"; // panel thickness (cross axis, grows with nesting depth)
			this._mainPosProp = isRow ? "top" : "left"; // position along the grouped dimension (main axis)
			this._crossPosProp = isRow ? "left" : "top"; // position along the cross axis

			this.$view.className += isRow
				? " webix_ssheet_row_groups"
				: " webix_ssheet_column_groups";

			const fixedContainerClass = isRow
				? "webix_ssheet_group_container_top"
				: "webix_ssheet_group_container_left";

			// fixed = frozen section (top for rows, left for columns)
			// scrollable = center/main section
			this._fixedContainer = webix.html.create("div", {
				class: `webix_ssheet_group_container ${fixedContainerClass}`,
			});
			this._scrollableContainer = webix.html.create("div", {
				class: "webix_ssheet_group_container webix_ssheet_group_container_center",
			});

			this.$view.appendChild(this._fixedContainer);
			this.$view.appendChild(this._scrollableContainer);

			webix.event(this.$view, "mouseover", e => this._handleHover(e));
			webix.event(this.$view, "mouseleave", () =>
				this._clearGroupHover()
			);
		},
		_getView() {
			return this.getTopParentView();
		},
		_getGrid() {
			return this._getView().$$("cells");
		},
		// operations/group.js calls this on init to set up the required handlers for UI-related interactions
		// data-based operations are handled in operations/group.js
		ready() {
			const view = this._getView();
			const grid = this._getGrid();
			const isRow = this._isRow;

			grid.attachEvent(isRow ? "onScrollY" : "onScrollX", () =>
				webix.delay(() => this._syncScroll())
			);

			grid.attachEvent(isRow ? "onRowResize" : "onColumnResize", () => {
				webix.delay(() => this.render());
			});

			view.attachEvent("onAction", a => {
				if (
					a === (isRow ? "freeze-row" : "freeze-column") ||
					a === "header-hide"
				)
					this.render();
			});

			view.attachEvent("onZoom", () => this.render());
		},
		render() {
			const view = this._getView();
			if (!view || !view.$$("cells")) return;
			// additional guard to prevent unnecessary re-renders on collapse/expand
			// e.g., when frozen state is reapplied
			if (view._groupSyncing) return;

			const isRow = this._isRow;
			const g = isRow ? "_row_groups" : "_col_groups";
			const groups = view[g] || [];
			const maxLevel = view[g + "_max_level"] || 0;

			this._fixedContainer.innerHTML = "";
			this._scrollableContainer.innerHTML = "";
			this._hoveredGroupIndex = null;
			this._adjustSize(maxLevel + 1);

			if (!groups.length) {
				this.hide();
				return;
			}

			this.show();

			const frozenBoundary = this._getFrozenBoundary();
			this._positions = {};

			const [fixedSize, scrollableSize] = this._buildPositions();

			this._fixedContainer.style[this._mainSizeProp] = `${fixedSize}px`;
			this._scrollableContainer.style[this._mainSizeProp] =
				`${scrollableSize}px`;

			const borderColorProp = isRow
				? "borderTopColor"
				: "borderLeftColor";

			if (fixedSize === 0) {
				this._fixedContainer.style.display = "none";
				this.$view.style[borderColorProp] = "inherit";
			} else {
				this._fixedContainer.style.display = "";
				this.$view.style[borderColorProp] = "transparent";
			}

			this._syncScroll();

			for (let i = 0; i < groups.length; i++) {
				this._renderGroup(i, groups, frozenBoundary);
			}
		},
		_getFrozenBoundary() {
			const grid = this._getGrid();

			if (this._isRow) {
				const topSplit = grid.config.topSplit;
				return topSplit > 0 ? grid.getIdByIndex(topSplit - 1) : 0;
			} else {
				const leftSplit = grid.config.leftSplit;
				// first column is the row-number column (frozen by default), ignore it
				return leftSplit > 1
					? grid.config.columns[leftSplit - 1].id
					: 0;
			}
		},
		_buildPositions() {
			const grid = this._getGrid();
			let fixedSize = 0;
			let scrollableSize = 0;

			if (this._isRow) {
				const view = this._getView();
				const topSplit = grid.config.topSplit;
				const base = grid.config.header
					? grid.config.headerRowHeight * view._zoom
					: 0;
				fixedSize = base;

				grid.data.each(item => {
					const h = item.$height || grid.config.rowHeight;
					if (item.id <= topSplit) {
						this._positions[item.id] = { pos: fixedSize, size: h };
						fixedSize += h;
					} else {
						this._positions[item.id] = {
							pos: scrollableSize,
							size: h,
						};
						scrollableSize += h;
					}
				});

				if (base) fixedSize += 1; // border alignment
			} else {
				const columns = grid.config.columns;
				const leftSplit = grid.config.leftSplit;

				for (let i = 0; i < columns.length; i++) {
					const col = columns[i];
					const w = col.width || grid.config.columnWidth;
					if (i < leftSplit) {
						this._positions[col.id] = { pos: fixedSize, size: w };
						fixedSize += w;
					} else {
						this._positions[col.id] = {
							pos: scrollableSize,
							size: w,
						};
						scrollableSize += w;
					}
				}
			}

			return [fixedSize, scrollableSize];
		},
		_renderGroup(index, groups, frozenBoundary) {
			const { start, end, level, open } = groups[index];
			const visualEnd = end + 1;
			const inFixed = frozenBoundary > 0 && start <= frozenBoundary;
			const inScrollable = visualEnd > frozenBoundary;

			// closed groups
			if (!open) {
				// when the group spans the very last row/column, visualEnd has no
				// position entry; fall back to the trailing edge of the last real element
				const btnPos =
					this._getPos(visualEnd) !== null
						? this._getPos(visualEnd)
						: this._getPos(end, "end");
				if (btnPos === null) return;
				this._renderButton(
					btnPos,
					open,
					level,
					index,
					inFixed ? this._fixedContainer : this._scrollableContainer
				);
				return;
			}

			// render visible segments of open groups
			const [visStart, visEnd] = this._getVisibleBounds(start, visualEnd);
			if (visStart > visEnd) return;

			// open groups
			if (inFixed) {
				const [fs, fe] = this._getVisibleBounds(
					visStart,
					Math.min(visEnd, frozenBoundary)
				);

				if (fs <= fe) {
					this._renderSegment(
						index,
						fs,
						fe,
						level,
						this._fixedContainer,
						{
							originStart: fs === start,
							originEnd: fe === visualEnd,
							toScrollable: inScrollable,
						}
					);
				}
			}

			if (inScrollable) {
				const [ss, se] = this._getVisibleBounds(
					Math.max(visStart, frozenBoundary + 1),
					visEnd
				);

				if (ss <= se) {
					this._renderSegment(
						index,
						ss,
						se,
						level,
						this._scrollableContainer,
						{
							originStart: ss === start,
							originEnd: se === visualEnd,
							fromFixed: inFixed,
						}
					);
				}
			}
		},
		_isVisible(id) {
			const view = this._getView();
			if (this._isRow) return view.isRowVisible(id);
			return view.isColumnVisible(id);
		},
		_getVisibleBounds(start, end) {
			let firstVisible = start;
			let lastVisible = end;

			for (let i = start; i <= end; i++) {
				if (this._positions[i] && this._isVisible(i)) {
					firstVisible = i;
					break;
				}
			}
			for (let i = end; i >= start; i--) {
				if (this._positions[i] && this._isVisible(i)) {
					lastVisible = i;
					break;
				}
			}

			return [firstVisible, lastVisible];
		},
		_renderSegment(groupIndex, start, end, level, container, segment) {
			const { originStart, originEnd, fromFixed, toScrollable } = segment;
			const offsetProp = this._isRow ? "offsetHeight" : "offsetWidth";

			const startPos = fromFixed
				? 0
				: this._getPos(start, originStart ? null : "start");
			const endPos = toScrollable
				? parseInt(container.style[this._mainSizeProp]) ||
					container[offsetProp]
				: this._getPos(end, originEnd ? null : "end");

			if (startPos === null || endPos === null) return;

			const mainSize = endPos - startPos;
			if (mainSize <= 0) return;

			const zoom = this._getView()._zoom;
			const levelSize = Math.round(groupSizes.levelSize * zoom);
			const btnSize = Math.round(groupSizes.btnSize * zoom);
			const crossPos =
				level * levelSize +
				Math.round(levelSize / 2) -
				groupSizes.lineOffset;
			// shorten the line so it meets the button centre without overlap
			const lineSize =
				!toScrollable && originEnd
					? mainSize - Math.round(btnSize / 2) + 2
					: mainSize;

			const lineClass = this._isRow
				? "webix_ssheet_group_line_vertical"
				: "webix_ssheet_group_line";

			const line = webix.html.create("div", {
				class: `${lineClass}${!originStart ? " webix_ssheet_group_line_continued" : ""}`,
				style: `${this._mainSizeProp}:${lineSize}px;${this._mainPosProp}:${startPos}px;${this._crossPosProp}:${crossPos}px;`,
				"data-group-index": groupIndex,
			});
			container.appendChild(line);

			if (!toScrollable && originEnd) {
				this._renderButton(endPos, true, level, groupIndex, container);
			}
		},
		_renderButton(pos, open, level, groupIndex, container) {
			const zoom = this._getView()._zoom;
			const levelSize = Math.round(groupSizes.levelSize * zoom);
			const btnSize = Math.round(groupSizes.btnSize * zoom);
			const mainPos = pos - Math.round(btnSize / 2) - 1;
			const crossPos =
				level * levelSize + Math.round((levelSize - btnSize) / 2);

			const btn = webix.html.create("div", {
				class: "webix_ssheet_group_btn",
				style: `${this._mainPosProp}:${mainPos}px;${this._crossPosProp}:${crossPos}px;width:${btnSize}px;height:${btnSize}px;`,
				"data-group-index": groupIndex,
			});
			btn.innerHTML = `<span class=${open ? "wxi-minus-square" : "wxi-plus-square"} style="font-size:${btnSize}px;display:block;"></span>`;
			container.appendChild(btn);
		},
		_getPos(id, edge) {
			const cached = this._positions[id];
			if (!cached) return null;
			const { pos, size } = cached;
			const offset =
				edge === "start"
					? 0
					: edge === "end"
						? size
						: Math.round(size / 2);
			return pos + offset;
		},
		_syncScroll() {
			const isRow = this._isRow;
			const scrollState = this._getGrid().getScrollState();
			const fixedSize =
				parseInt(this._fixedContainer.style[this._mainSizeProp]) || 0;
			const dir = isRow ? "y" : "x";
			const delta = fixedSize - scrollState[dir];
			this._scrollableContainer.style.transform = isRow
				? `translateY(${delta}px)`
				: `translateX(${delta}px)`;
		},
		_adjustSize(levels) {
			const zoom = this._getView()._zoom;
			const size = levels > 0 ? levels * groupSizes.levelSize * zoom : 0;

			if (this.config[this._crossSizeProp] !== size) {
				this.define(this._crossSizeProp, size);
				this.resize();
			}
		},
		_handleHover(e) {
			const target = e.target.closest("[data-group-index]");
			const ind = target ? target.getAttribute("data-group-index") : null;
			if (ind === this._hoveredGroupIndex) return;
			this._clearGroupHover();
			this._hoveredGroupIndex = ind;

			if (ind !== null) {
				this.$view
					.querySelectorAll(`[data-group-index="${ind}"]`)
					.forEach(el =>
						el.classList.add("webix_ssheet_group_hovered")
					);
			}
		},
		_clearGroupHover() {
			this.$view
				.querySelectorAll(".webix_ssheet_group_hovered")
				.forEach(el =>
					el.classList.remove("webix_ssheet_group_hovered")
				);
			this._hoveredGroupIndex = null;
		},
		_handleClick(e) {
			if (this.config.readonly) return;
			const target = e.target.closest("[data-group-index]");

			if (target) {
				const ind = parseInt(target.getAttribute("data-group-index"));
				toggleGroup(this._getView(), this.config.dimension, ind);
			}
		},
	},
	webix.MouseEvents,
	webix.EventSystem,
	webix.ui.view
);
